/**
 * @fileoverview Professional profanity filter for username validation
 *
 * Custom implementation for comprehensive filtering without external dependencies.
 */

// Comprehensive list of inappropriate words
const INAPPROPRIATE_WORDS = [
  // System/Reserved words
  'admin', 'administrator', 'mod', 'moderator', 'system',
  'bot', 'official', 'support', 'staff', 'owner', 'root',
  'null', 'undefined', 'test', 'debug', 'console',

  // Gaming toxicity
  'kys', 'ez', 'rekt', 'pwned', 'noob', 'scrub',

  // Additional inappropriate
  'zodiac', // Don't allow brand name

  // Common profanity (basic list)
  'fuck', 'shit', 'damn', 'hell', 'ass', 'bitch', 'bastard',
  'crap', 'piss', 'cock', 'dick', 'pussy', 'fag', 'slut',
  'whore', 'cunt', 'nigger', 'nigga', 'retard', 'wtf',
];

/**
 * Check if a username contains inappropriate content
 */
export function containsInappropriateContent(username: string): boolean {
  if (!username || typeof username !== 'string') {
    return false;
  }

  const trimmedUsername = username.trim().toLowerCase();

  // Check for direct matches
  for (const word of INAPPROPRIATE_WORDS) {
    if (trimmedUsername.includes(word)) {
      return true;
    }
  }

  // Check for leet speak variations
  const leetSubstitutions: { [key: string]: string } = {
    '0': 'o',
    '1': 'i',
    '3': 'e',
    '4': 'a',
    '5': 's',
    '7': 't',
    '@': 'a',
    '$': 's'
  };

  let normalizedUsername = trimmedUsername;
  Object.entries(leetSubstitutions).forEach(([leetChar, normalChar]) => {
    normalizedUsername = normalizedUsername.replace(new RegExp(leetChar, 'g'), normalChar);
  });

  // Check normalized version
  for (const word of INAPPROPRIATE_WORDS) {
    if (normalizedUsername.includes(word)) {
      return true;
    }
  }

  return false;
}

/**
 * Get a clean alternative username if the provided one is inappropriate
 */
export function getCleanAlternative(): string {
  const cleanAlternatives = [
    'player', 'gamer', 'user', 'competitor', 'challenger',
    'champion', 'warrior', 'master', 'rookie', 'veteran',
    'knight', 'rook', 'bishop', 'queen', 'king'
  ];

  return cleanAlternatives[Math.floor(Math.random() * cleanAlternatives.length)] +
    Math.floor(Math.random() * 1000);
}

/**
 * Clean a username by removing profanity (replaces with ***)
 */
export function cleanUsername(username: string): string {
  if (!username || typeof username !== 'string') {
    return '';
  }

  let cleanedUsername = username;
  const lowerUsername = username.toLowerCase();

  for (const word of INAPPROPRIATE_WORDS) {
    // Find all case-insensitive matches
    const regex = new RegExp(word, 'gi');
    const matches = lowerUsername.match(regex);

    if (matches) {
      matches.forEach(() => {
        cleanedUsername = cleanedUsername.replace(regex, '*'.repeat(word.length));
      });
    }
  }

  return cleanedUsername;
}

/**
 * Validate username for appropriateness and return result with suggestion
 */
export function validateUsernameContent(username: string): {
  isValid: boolean;
  suggestion?: string;
  reason?: string;
} {
  if (containsInappropriateContent(username)) {
    return {
      isValid: false,
      suggestion: getCleanAlternative(),
      reason: 'Username contains inappropriate content'
    };
  }

  return { isValid: true };
}
